// GEOPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/seoxs.PNG'

const GEOPage = () => {
    useEffect(() => {
        window.scroll(0, 0)

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const geoStats = [
        { number: '40%', label: 'of users now use AI search tools daily' },
        { number: '10x', label: 'higher engagement from AI citations vs traditional links' },
        { number: '60%', label: 'of business queries answered by AI platforms' },
        { number: '3.5x', label: 'more brand authority with structured data' }
    ];

    const geoApproaches = [
        {
            icon: 'fas fa-search',
            title: 'GEO Audit & Insight',
            desc: 'We review your current presence across AI search platforms to identify gaps in citations, misinformation risks, and areas for improvement. This forms the foundation for a tailored GEO strategy.'
        },
        {
            icon: 'fas fa-lightbulb',
            title: 'Strategic AI Optimisation',
            desc: 'We build a strategic roadmap that aligns structured data, entity signals and contextual relevance with AI search behaviour — so platforms can accurately interpret and surface your brand.'
        },
        {
            icon: 'fas fa-file-alt',
            title: 'Content Designed for AI',
            desc: 'We craft structured content that responds directly to user intent in a format generative engines prioritise. This includes AI-ready FAQs, fact blocks, authoritative guides, and entity-centric information hubs.'
        },
        {
            icon: 'fas fa-cogs',
            title: 'Structured Data & Schema Implementation',
            desc: 'Technical foundations like schema markup and AI-specific metadata are essential for machine readability. We implement and maintain these systems so AI crawlers can parse and prioritise your content.'
        },
        {
            icon: 'fas fa-link',
            title: 'High-Authority Resource Development',
            desc: 'We create research-backed articles, insightful thought leadership pieces, and shareable digital assets that establish your brand as a trusted reference point for AI systems.'
        },
        {
            icon: 'fas fa-chart-line',
            title: 'Continuous Monitoring',
            desc: 'AI search is fast-moving. We continually track how and where your brand appears in AI outputs and adapt strategies to stay ahead of algorithm changes and competitive shifts.'
        }
    ];

    const benefits = [
        ['AI Discovery', 'Your brand becomes visible across ChatGPT, Gemini, Perplexity and AI summary features'],
        ['Greater Authority', 'Structured, trustworthy data improves trust signals for both AI and users'],
        ['Deeper Engagement', 'Users receive accurate, helpful answers and associate your brand with expertise'],
        ['Future-Ready Visibility', 'You stay ahead as search behavior shifts toward generative discovery']
    ];

    const targetBusinesses = [
        'Tech companies and SaaS platforms',
        'E-commerce and high-volume content publishers',
        'Professional services and consultancies',
        'Brands prioritising long-term organic growth',
        'Organisations in competitive search landscapes'
    ];

    const geoQuestions = [
        'How does your brand appear in AI-generated answers and summaries?',
        'Is your content structured for generative engines to reference and cite?',
        'What authority signals do AI platforms recognize about your brand?',
        'Are you visible in ChatGPT, Gemini, and other AI search tools?',
        'How can you become the source AI chooses, not just the site that ranks?'
    ];

    return (
        <div className="seo sv-page">
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">GEO SEO</span> Services
                    </h1>
                    <p>Generative Engine Optimisation for AI-Driven Search</p>
                </div>
            </div>

            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    <span className="sv-keyword-highlight">Generative Engine Optimisation (GEO)</span> is becoming essential for visibility in the age of AI search. AI platforms like ChatGPT, Google's Search Generative Experience and other generative interfaces answer queries directly using trusted sources.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, our <strong>GEO SEO services</strong> help businesses move beyond rankings to become the brand that AI recommends and references. Unlike traditional SEO, GEO focuses on how AI systems understand, interpret and use your content as part of the answer itself.
                                </p>
                                <p className="sv-description">
                                    GEO ensures your brand is included, cited and trusted within AI-generated responses — not just appearing in traditional search results.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start GEO SEO Audit
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Free Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">GEO SEO Matters</span> for Your Business
                        </h2>
                        <Row className="sv-stats-grid">
                            {geoStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is GEO SEO?</h2>
                                <p className="sv-section-description">
                                    <strong>Generative Engine Optimisation (GEO)</strong> is the practice of preparing your digital presence so that AI-powered search tools include, cite and prioritise your brand when generating answers to user queries. These systems don't just index pages — they analyse meaning, context, and structured knowledge before delivering responses.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Questions Our GEO SEO Services Answer:</h4>
                                    {geoQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Your Business Needs GEO SEO</h2>
                                <p className="sv-section-description">
                                    Traditional Search Engine Optimisation still matters, but it no longer captures the full picture of digital discovery. GEO SEO enables your brand to be visible in AI-generated answers rather than just in search result links.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-eye"></i>
                                        <h5>AI Visibility</h5>
                                        <p>Be visible in AI-generated answers across ChatGPT, Gemini and AI summary features</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-badge-check"></i>
                                        <h5>Authority Signals</h5>
                                        <p>Strengthen authority signals across structured data and entity relationships</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-book-open"></i>
                                        <h5>Earn Citations</h5>
                                        <p>Earn citations and references within conversational AI responses</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-bullseye"></i>
                                        <h5>Intent Alignment</h5>
                                        <p>Align content with how generative engines interpret information</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our GEO SEO Approach at Reachtix</h2>
                                <p className="sv-section-description">
                                    We combine strategy, technical implementation, and content design to ensure your brand is effectively optimised for next-generation search.
                                </p>

                                <div className="sv-approaches-grid">
                                    {geoApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Benefits of GEO SEO With Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Core Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Should Invest in GEO SEO?</h2>
                                <p className="sv-section-description">
                                    Our <strong>GEO SEO services</strong> are ideal for businesses that want leadership in digital visibility:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    By integrating GEO into your search strategy, you move from being discovered to being recommended — the next frontier of digital visibility.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Own Your Place in AI Search?</h2>
                                <p className="sv-cta-description">
                                    AI-driven discovery is already reshaping how customers find answers. If your brand isn't part of the AI narrative, you risk being invisible where it matters most.
                                </p>
                                <p className="sv-cta-description">
                                    Reachtix's GEO SEO services help your business become the source AI chooses, not just the site that ranks. Let's build a search presence that's future-proof and AI-ready.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-envelope mr-2"></i>
                                        Contact Us Today
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/services/seo"
                                        color='dark'
                                    >
                                        <i className="fas fa-grid mr-2"></i>
                                        Explore All SEO Services
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default GEOPage;